import {
  memo,
  useMemo
} from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { type BaseCfComponent, type CfPageMetadata } from '@rippling/sava-ui/src/types';
import dynamic from 'next/dynamic';

import SavaLayout from ':templates/sava/SavaLayout';
import { type BasePageProps } from ':types/page';

export interface LandingPageProps extends BasePageProps {
  data: {
    fields: {
      content: (BaseCfComponent & Record<string, unknown>)[]
      pageMetadata: CfPageMetadata;
    }
    urlPath: null | string;
  } & BaseCfComponent,
}

// Definition of all async components
const asyncComponents = {
  popUpModal: dynamic(() => import('@rippling/sava-ui/src/components/molecules/PopupModal')),
  uiAccoladesSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/AccoladesSection')),
  uiCardsSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/Cards')),
  uiCenteredHighlightSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/CenteredHighlight')),
  uiCtaSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/CtaSection')),
  uiGridListSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/GridListSection')),
  uiHeroSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/Hero')),
  uiListSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/ListSection')),
  uiOverviewSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/Overview')),
  uiPlatformSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/PlatformSection')),
  uiProductOverviewSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/ProductOverview')),
  uiRiverFlowSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/RiverFlow')),
  uiStatsSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/Stats')),
  uiTable: dynamic(() => import('@rippling/sava-ui/src/components/organisms/TableSection')),
  uiTabletCarousel: dynamic(() => import('@rippling/sava-ui/src/components/organisms/TabletCarousel')),
  uiTabs: dynamic(() => import('@rippling/sava-ui/src/components/organisms/TabsSection')),
  uiTestimonialSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/TestimonialsSection')),
  uiTextSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/TextSection')),
  uiTimelineSection: dynamic(() => import('@rippling/sava-ui/src/components/organisms/Timeline')),
};

const LandingPage = ({
  data,
  draftMode,
  enableLivePreview,
  globals,
  locale,
  localePaths,
  prodCssFiles,
}: LandingPageProps) => {
  const {
    fields: { content, pageMetadata }, sys, urlPath,
  } = data;

  const renderableContent = useMemo(() => {
    return content.filter((section) => {
      return asyncComponents[section.sys.contentType.sys.id];
    });
  }, [content]);

  const updatedContent = enableLivePreview && draftMode
    ? useContentfulLiveUpdates(renderableContent)
    : renderableContent;

  return (
    <SavaLayout
      draftMode={draftMode}
      globals={globals}
      locale={locale}
      localePaths={localePaths}
      pageMetadata={pageMetadata}
      pageSys={sys}
      prodCssFiles={prodCssFiles}
      urlPath={urlPath ?? ''}
    >
      {updatedContent.map((props) => {
        const { contentType: { sys: { id: contentType } }, id } = props.sys;
        const Component = asyncComponents[contentType];

        return <Component {...props} key={id} />;
      })}
    </SavaLayout>
  );
};

export default memo(LandingPage);
